import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../config/firebaseConfig";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { Loader, Plus, Edit2, Trash2, X } from "lucide-react";

const EMPTY_FORM = {
  post: "",
  experience: "",
  extraJson: "{}",
};

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "{}";
  }
}

function getEditablePayload(job) {
  const payload = { ...job };
  delete payload.id;
  delete payload.Post;
  delete payload.Experience;
  delete payload.createdAt;
  delete payload.updatedAt;
  delete payload.createdBy;
  delete payload.updatedBy;
  return payload;
}

export default function ManageVacancies() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ success: "", error: "" });

  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const roles = userSnap.exists() ? userSnap.data()?.roles || [] : [];
        const canManage =
          roles.includes("admin") ||
          roles.includes("principal") ||
          roles.includes("ir");

        setIsAdmin(canManage);

        if (!canManage) {
          navigate("/");
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        navigate("/");
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const snapshot = await getDocs(collection(db, "JobsList"));
        const data = snapshot.docs.map((jobDoc) => ({
          id: jobDoc.id,
          ...jobDoc.data(),
        }));
        setJobs(data);
      } catch (error) {
        console.error("Error fetching vacancies:", error);
        setJobs([]);
      }
      setDataLoading(false);
    };

    fetchJobs();
  }, []);

  const openCreateForm = () => {
    setEditingJob(null);
    setFormData(EMPTY_FORM);
    setMessage({ success: "", error: "" });
    setShowForm(true);
  };

  const openEditForm = (job) => {
    setEditingJob(job);
    setFormData({
      post: job.Post || "",
      experience: job.Experience || "",
      extraJson: safeStringify(getEditablePayload(job)),
    });
    setMessage({ success: "", error: "" });
    setShowForm(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveJob = async () => {
    if (!formData.post.trim()) {
      setMessage({ success: "", error: "Job title is required." });
      return;
    }

    let extraData = {};
    if (formData.extraJson.trim()) {
      try {
        extraData = JSON.parse(formData.extraJson);
      } catch (error) {
        setMessage({
          success: "",
          error: "Extra data must be valid JSON.",
        });
        return;
      }
    }

    if (
      extraData === null ||
      Array.isArray(extraData) ||
      typeof extraData !== "object"
    ) {
      setMessage({
        success: "",
        error: "Extra data must be a JSON object.",
      });
      return;
    }

    setSaving(true);
    setMessage({ success: "", error: "" });

    try {
      const payload = {
        Post: formData.post.trim(),
        Experience: formData.experience.trim(),
        ...extraData,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      };

      if (editingJob) {
        await updateDoc(doc(db, "JobsList", editingJob.id), payload);
        setJobs((prev) =>
          prev.map((job) =>
            job.id === editingJob.id ? { ...job, ...payload } : job,
          ),
        );
        setMessage({ success: "Vacancy updated successfully.", error: "" });
      } else {
        const created = await addDoc(collection(db, "JobsList"), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: user?.uid || null,
        });
        setJobs((prev) => [
          {
            id: created.id,
            Post: payload.Post,
            Experience: payload.Experience,
            ...extraData,
          },
          ...prev,
        ]);
        setMessage({ success: "Vacancy created successfully.", error: "" });
      }

      setShowForm(false);
      setEditingJob(null);
      setFormData(EMPTY_FORM);
    } catch (error) {
      console.error("Failed to save vacancy:", error);
      setMessage({
        success: "",
        error: "Failed to save vacancy. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Delete vacancy "${job.Post}"?`)) {
      return;
    }

    setSaving(true);
    setMessage({ success: "", error: "" });

    try {
      await deleteDoc(doc(db, "JobsList", job.id));
      setJobs((prev) => prev.filter((item) => item.id !== job.id));
      setMessage({ success: "Vacancy deleted successfully.", error: "" });
    } catch (error) {
      console.error("Failed to delete vacancy:", error);
      setMessage({
        success: "",
        error: "Failed to delete vacancy. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 text-center text-slate-600">
        You do not have permission to access this page.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-md sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-600">
              Careers Admin
            </p>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Manage Vacancies
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Edit the job postings that appear on the public vacancies page.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Add Vacancy
          </button>
        </div>

        {message.success && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
            {message.success}
          </div>
        )}
        {message.error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {message.error}
          </div>
        )}

        <div className="rounded-3xl bg-white p-4 shadow-md sm:p-6">
          {dataLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              No vacancies found. Add the first opening.
            </div>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="space-y-2">
                    <h2 className="text-xl font-bold text-slate-900">
                      {job.Post || "Untitled Vacancy"}
                    </h2>
                    <p className="text-sm text-slate-600">
                      {job.Experience || "No experience summary added yet."}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 self-start">
                    <button
                      type="button"
                      onClick={() => openEditForm(job)}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => removeJob(job)}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 py-6">
          <div className="w-full max-w-3xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingJob ? "Edit Vacancy" : "Add Vacancy"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  The public page will render every saved field.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Job Title / Post
                </label>
                <input
                  type="text"
                  name="post"
                  value={formData.post}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500"
                  placeholder="Example: Assistant Teacher"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Experience Summary
                </label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  rows={3}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500"
                  placeholder="Example: 2-4 years in primary teaching preferred"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Extra Data as JSON
                </label>
                <textarea
                  name="extraJson"
                  value={formData.extraJson}
                  onChange={handleChange}
                  rows={10}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 font-mono text-sm outline-none transition focus:border-blue-500"
                  placeholder='{"Job Description": "...", "Last Date": "..."}'
                />
                <p className="mt-2 text-xs text-slate-500">
                  Use this for any extra fields you want to show on the public
                  vacancies page.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-xl border border-slate-300 px-4 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveJob}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader className="h-5 w-5 animate-spin" /> : null}
                {editingJob ? "Update Vacancy" : "Create Vacancy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
